import {
  AnalysisResult,
  CoreAnalysisEngine,
  RecordingResponse,
} from "./coreAnalysisEngine";
import { ReactElement, useContext, useEffect } from "react";
import { TFunction } from "i18next";
import { ResultDashboard } from "../components/molecules/ResultDashboard";
import {
  AnalysisEngineType,
  EngineType,
  ResultDashboardData,
  ResultDiagramIndex,
  ResultMainParam,
  ResultSubParam,
  ResultSupplementaryInfo,
} from "../types";
import { Mi2AnalysisResult, MI2_DATA } from "./mi2Engine";
import { ImageContext } from "../components/atoms/ImageProvider";
import PictogramLowImgUrl from "../assets/images/pictogram/low.png";
import PictogramMediumImgUrl from "../assets/images/pictogram/medium.png";
import PictogramHighImgUrl from "../assets/images/pictogram/high.png";
import { Image } from "@chakra-ui/react";
import { Center } from "@chakra-ui/react";

type Mi21MdviCfDetail = Mi2AnalysisResult["analysis_result"] & {
  mi2_data: MI2_DATA[];
};

export type Mi21AnalysisResult = AnalysisResult<"mi2.1">;
export type Mi21DetailedAnalysisResult = {
  mdvi_cf: Record<string, Mi21MdviCfDetail>;
  mi2_1: {
    MDVI: number;
    CPF: number;
    CTP: number[];
  };
};

const DEFAULT_DASHBOARD_MAIN_PARAM: ResultMainParam = {
  title: "Result.Mi2_1_result_title",
  maxWidth: "180px",
  // border: "solid 3px #F2CA95",
  value: undefined,
};

const SUB_PARAMS_TITLE_LIST = [
  "Result.Mi2_1_score",
  "Result.Mi2_cpf",
  "Result.Mi2_zh",
];

const SUPPLEMENTARY_INFO_TITLE_LIST = [
  "Result.analyze_files",
  "Result.analyze_utterances",
];

export function ConvertResult({
  result,
}: {
  result: Mi21AnalysisResult;
}): Record<string, number | string> {
  if (!result) {
    return {
      zh_score: "-",
      cpf_score: "-",
    };
  }
  const detailed = result.detailed_analysis_result as
    Mi21DetailedAnalysisResult | undefined;
  const zh_score = Math.floor((detailed?.mi2_1.MDVI ?? 0) * 100);
  const cpf_score = Math.floor((detailed?.mi2_1.CPF ?? 0) * 100);
  return {
    zh_score: zh_score,
    cpf_score: cpf_score,
  };
}

interface EngineProps {
  _analysisType?: AnalysisEngineType | null;
}

function Mi21ResultImage({
  label,
}: Readonly<{ label: "A" | "B" | "C" }>): ReactElement {
  const { imageSrc, preloadImage } = useContext(ImageContext);

  useEffect(() => {
    if (imageSrc && !imageSrc.PictogramImg[label]) {
      if (preloadImage) {
        preloadImage("PictogramImg", {
          A: PictogramHighImgUrl,
          B: PictogramMediumImgUrl,
          C: PictogramLowImgUrl,
        });
      }
    }
  }, [imageSrc, preloadImage, label]);
  // 画像を中央に配置するためにCenterでラップ

  return (
    <Center>
      <Image
        src={imageSrc ? imageSrc.PictogramImg[label] : undefined}
        width="80px"
        height="auto"
      />
    </Center>
  );
}

export class Mi21Engine extends CoreAnalysisEngine<"mi2.1"> {
  _engineType: EngineType = "mi2.1";
  _analysisType: AnalysisEngineType | null = null;

  constructor(props?: EngineProps) {
    super("mi2.1");
    if (props && props._analysisType) {
      this._analysisType = props._analysisType;
    }
  }

  private static dashboardDiagramIndex(t: TFunction): ResultDiagramIndex[] {
    return [
      { width: "32px", title: "No" },
      { width: "48%", title: t("Result.table_phrase"), align: "left" },
      { width: "15%", title: "CpF", textTransform: "none" },
      { width: "15%", title: "Z-H", textTransform: "none" },
      // { width: "12%", title: "hurst" },
      // { width: "10%", title: "zcr" },
      { width: "15%", title: "sec" },
    ];
  }

  private static errorDashboardData(t: TFunction): ResultDashboardData {
    return {
      mainParam: DEFAULT_DASHBOARD_MAIN_PARAM,
      subParams: [
        {
          title: SUB_PARAMS_TITLE_LIST[0],
          value: undefined,
        },
        {
          title: SUB_PARAMS_TITLE_LIST[1],
          value: undefined,
        },
        {
          title: SUB_PARAMS_TITLE_LIST[2],
          value: undefined,
        },
      ],
      supplementaryInfo: [
        {
          title: SUPPLEMENTARY_INFO_TITLE_LIST[0],
          value: undefined,
        },
        {
          title: SUPPLEMENTARY_INFO_TITLE_LIST[1],
          value: undefined,
        },
      ],
      diagramIndex: this.dashboardDiagramIndex(t),
      diagramParams: [],
    };
  }

  private static convertToDashboardData(
    result: Mi21AnalysisResult,
    recording: RecordingResponse | null,
    t: TFunction,
  ): ResultDashboardData {
    const phraseByFilename = new Map(
      recording?.files.map((f) => [f.filename, f.phrase]) ?? [],
    );

    const detailed = result.detailed_analysis_result as
      Mi21DetailedAnalysisResult | undefined;

    let mi2_data: MI2_DATA[] = [];
    let utterances = 0;
    if (detailed?.mdvi_cf) {
      for (const filename in detailed.mdvi_cf) {
        const res = detailed.mdvi_cf[filename];
        mi2_data = mi2_data.concat(res.mi2_data);
        utterances += res.utterances;
      }
    }

    const diagramParams = CoreAnalysisEngine.generateResultDiagramParams(
      mi2_data,
      (param, recordId, _phraseIndex) => [
        { width: "32px", value: recordId, align: "left" },
        {
          width: "48%",
          value: phraseByFilename.get(param.wav_filename) ?? "Error",
          align: "left",
        },
        {
          width: "10%",
          value: Math.floor(param.cpf_value * 100),
        },
        {
          width: "10%",
          value: Math.floor(param.zh_value * 100),
        },
        {
          width: "10%",
          value: ((param.end_time - param.start_time) / 1000).toFixed(1),
        },
      ],
    );

    // decision 0:うつ・ストレス 1:健常
    const result_str =
      result.analysis_result.decision == 0
        ? t("Result.Mi2_1_result_positive")
        : t("Result.Mi2_1_result_negative");

    const label = result.analysis_result.decision == 0 ? "C" : "A";
    const mainParam: ResultMainParam = {
      ...DEFAULT_DASHBOARD_MAIN_PARAM,
      value: result_str,
      valueNode: <Mi21ResultImage label={label} />,
    };

    const subParams: ResultSubParam[] = [
      {
        title: SUB_PARAMS_TITLE_LIST[0],
        value: `${Math.floor(result.analysis_result.score * 100)}`,
      },
      {
        title: SUB_PARAMS_TITLE_LIST[1],
        value: `${Math.floor((detailed?.mi2_1.CPF ?? 0) * 100)}`,
      },
      {
        title: SUB_PARAMS_TITLE_LIST[2],
        value: `${Math.floor((detailed?.mi2_1.MDVI ?? 0) * 100)}`,
      },
    ];

    const supplementaryInfo: ResultSupplementaryInfo[] = [
      {
        title: SUPPLEMENTARY_INFO_TITLE_LIST[0],
        value: recording?.files.length ?? 0,
      },
      {
        title: SUPPLEMENTARY_INFO_TITLE_LIST[1],
        value: utterances,
      },
    ];

    return {
      mainParam,
      subParams,
      supplementaryInfo,
      diagramIndex: this.dashboardDiagramIndex(t),
      diagramParams,
    };
  }

  async renderResult(
    key: number,
    fileId: string,
    t: TFunction,
  ): Promise<ReactElement> {
    const result = await this.getResult(fileId);
    const recording = result ? await this.getRecording(fileId) : null;
    const dashboardData = result
      ? Mi21Engine.convertToDashboardData(result, recording, t)
      : Mi21Engine.errorDashboardData(t);

    return (
      <ResultDashboard
        key={key}
        title={t("Result.Mi2_analysis_result")}
        result={dashboardData}
      />
    );
  }
}
